import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import FishSearchSelect, { FishOption } from '@Components/FishSearchSelect';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import service from '../../service';
import {
  useFlowDebugInstances,
  useFlowDebugLogs,
  type FlowTraceEntry,
} from '../../hooks/useFlowDebug';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import type { AttachedInbox } from '@Types/triggers';

const ACTIVITY_TYPES = [
  'Booked Event',
  'Reservation',
  'Ticketed Event',
  'Hotel Stay',
] as const;

type EmitMode = 'activity-created' | 'activity-updated' | 'lapse' | 'upcoming' | 'inbound-sms';

interface DebugTemplate {
  id: string;
  name: string;
  mode: EmitMode;
  fish: FishOption | null;
  activity: {
    type: (typeof ACTIVITY_TYPES)[number];
    location: string;
    space: string;
    status: string;
    amount: string;
    eventType: string;
    activityDate: string;
    bookDate: string;
  };
  lapseStageId: string;
  evaluate: boolean;
  lastStageExecutedAt: string;
  // Absent on templates saved before the Inbound SMS tab existed.
  inboundSms?: { inboxId: string; body: string };
  // Absent on templates saved before the Upcoming tab existed.
  upcoming?: { stageId: string; entityId: string };
}

interface FlowStageLike {
  id: string;
  title: string;
  flowStageTypeId?: string;
}

interface FlowStageTypeLike {
  id: string;
  name: string;
  // Schedule settings of an upcoming stage type; null for other types.
  upcoming?: unknown | null;
}

interface UpcomingEmitResult {
  emitted: number;
  skippedReason?: string;
  counts?: Record<string, number>;
  // The Fish the emitted occurrence belongs to.
  fishId?: string | null;
}

// Why an upcoming debug run sent nothing: the skip reason, or the non-zero step counts.
const describeUpcomingMiss = (result: UpcomingEmitResult): string => {
  if (result.skippedReason) return result.skippedReason;
  const steps = Object.entries(result.counts ?? {})
    .filter(([, n]) => n > 0)
    .map(([step, n]) => `${step} ${n}`);
  if (steps.length === 0) return "not found in the stage's date range or filters";
  return steps.join(', ');
};

interface DebugInboxRow {
  id: string;
  inboxType: string;
  phoneNumber: string | null;
  ownerName: string;
}

interface FlowDebugSlideoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId?: string;
  flowStages: FlowStageLike[];
  flowStageTypes: FlowStageTypeLike[];
}

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const toISO = (localValue?: string) =>
  localValue ? new Date(localValue).toISOString() : null;

const OUTCOME_STYLES: Record<string, string> = {
  'stage-executed': 'bg-green-100 text-green-800',
  'trigger-fired': 'bg-emerald-100 text-emerald-800',
  'skipped:condition': 'bg-amber-100 text-amber-800',
  'skipped:dedup': 'bg-gray-200 text-gray-700',
  'trigger-skipped': 'bg-amber-100 text-amber-800',
  'no-stages-matched': 'bg-red-100 text-red-800',
  'instance-error': 'bg-red-200 text-red-900',
};

const fieldClass =
  'mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm';
const labelClass = 'block text-xs font-medium text-gray-600';

const FlowDebugSlideout = ({
  open,
  onOpenChange,
  flowId,
  flowStages,
  flowStageTypes,
}: FlowDebugSlideoutProps) => {
  const [fish, setFish] = useState<FishOption | null>(null);
  const [mode, setMode] = useState<EmitMode>('activity-created');
  const [emitting, setEmitting] = useState(false);

  // Activity form state (mirrors the standalone activity/test screen).
  const [activityId, setActivityId] = useState<string>(() => crypto.randomUUID());
  const [type, setType] =
    useState<(typeof ACTIVITY_TYPES)[number]>('Reservation');
  const [location, setLocation] = useState('');
  const [space, setSpace] = useState('');
  const [status, setStatus] = useState('');
  const [amount, setAmount] = useState('');
  const [eventType, setEventType] = useState('');
  const [activityDate, setActivityDate] = useState(nowLocal);
  const [bookDate, setBookDate] = useState('');
  const [attachedInboxId, setAttachedInboxId] = useState('');
  const [stylistName, setStylistName] = useState('');
  const [leadOwnerName, setLeadOwnerName] = useState('');
  const [roomTypes, setRoomTypes] = useState('');
  const [lastEmitFrom, setLastEmitFrom] = useState<string | null>(null);
  const [lastEmitResolved, setLastEmitResolved] = useState<boolean | null>(null);
  const inboxesQuery = useQuery<DebugInboxRow[]>({
    queryKey: ['inbox-management'],
    queryFn: () => service.getInboxManagement(),
    enabled: open,
  });
  const inboxOptions = (inboxesQuery.data ?? [])
    .filter((i) => i.inboxType === 'SMS' && i.phoneNumber)
    .map((i) => ({ value: i.id, label: `${i.phoneNumber} — ${i.ownerName}` }));

  // Lapse form state.
  const lapseStages = useMemo(() => {
    const lapseTypeIds = new Set(
      flowStageTypes
        .filter((t) => t.name === 'LAPSE_DETECTED')
        .map((t) => t.id),
    );
    return flowStages.filter((s) => lapseTypeIds.has(s.flowStageTypeId ?? ''));
  }, [flowStages, flowStageTypes]);
  const [lapseStageId, setLapseStageId] = useState('');
  // When set, the backend runs the single-fish lapse check and only emits if the
  // fish genuinely qualifies (reporting why not otherwise).
  const [evaluate, setEvaluate] = useState(true);
  // Optional datetime-local override for the fish's predecessor execution time.
  const [lastStageExecutedAt, setLastStageExecutedAt] = useState('');
  const [lastLapseResult, setLastLapseResult] = useState<{
    emitted: boolean;
    reason?: string;
  } | null>(null);

  // Inbound SMS form state: the inbox the test fish texts, and what they say.
  const [smsInboxId, setSmsInboxId] = useState('');
  const [smsBody, setSmsBody] = useState('');
  const [lastSmsResult, setLastSmsResult] = useState<{
    emitted: boolean;
    resolvedFishId: string | null;
  } | null>(null);

  // Upcoming form state: the upcoming stage, and the activity or subscription to run
  // it for. Evaluate (shared with lapse) applies the sweep's due-time check.
  const upcomingStages = useMemo(() => {
    const upcomingTypeIds = new Set(
      flowStageTypes.filter((t) => t.upcoming).map((t) => t.id),
    );
    return flowStages.filter((s) => upcomingTypeIds.has(s.flowStageTypeId ?? ''));
  }, [flowStages, flowStageTypes]);
  const [upcomingStageId, setUpcomingStageId] = useState('');
  const [upcomingEntityId, setUpcomingEntityId] = useState('');
  const [lastUpcomingResult, setLastUpcomingResult] =
    useState<UpcomingEmitResult | null>(null);

  // Emit templates, persisted to localStorage (this device only).
  const [templates, setTemplates] = useLocalStorage<DebugTemplate[]>(
    'flowDebug.templates',
    [],
  );
  const [templateName, setTemplateName] = useState('');

  const saveTemplate = () => {
    const name = templateName.trim();
    if (!name) return;
    const template: DebugTemplate = {
      id: crypto.randomUUID(),
      name,
      mode,
      fish,
      activity: {
        type,
        location,
        space,
        status,
        amount,
        eventType,
        activityDate,
        bookDate,
      },
      lapseStageId,
      evaluate,
      lastStageExecutedAt,
      inboundSms: { inboxId: smsInboxId, body: smsBody },
      upcoming: { stageId: upcomingStageId, entityId: upcomingEntityId },
    };
    // Replace any existing template with the same name.
    setTemplates((prev) => [...prev.filter((t) => t.name !== name), template]);
    setTemplateName('');
    toastSuccess(`Saved template "${name}"`);
  };

  const applyTemplate = (template: DebugTemplate) => {
    setMode(template.mode);
    setFish(template.fish ?? null);
    setType(template.activity.type);
    setLocation(template.activity.location);
    setSpace(template.activity.space);
    setStatus(template.activity.status);
    setAmount(template.activity.amount);
    setEventType(template.activity.eventType);
    setActivityDate(template.activity.activityDate);
    setBookDate(template.activity.bookDate);
    setLapseStageId(template.lapseStageId);
    setEvaluate(template.evaluate ?? true);
    setLastStageExecutedAt(template.lastStageExecutedAt ?? '');
    setSmsInboxId(template.inboundSms?.inboxId ?? '');
    setSmsBody(template.inboundSms?.body ?? '');
    setUpcomingStageId(template.upcoming?.stageId ?? '');
    setUpcomingEntityId(template.upcoming?.entityId ?? '');
    // Fresh activity id so applying + emitting isn't deduped as a repeat event.
    setActivityId(crypto.randomUUID());
  };

  const deleteTemplate = (id: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  const fishId = fish?.value;

  const logsQuery = useFlowDebugLogs({
    flowId,
    fishId,
    enabled: open,
  });
  const instancesQuery = useFlowDebugInstances({
    flowId,
    fishId,
    enabled: open,
  });

  const entries: FlowTraceEntry[] = (logsQuery.data?.data ?? [])
    .slice()
    .reverse();
  const instances: any[] = instancesQuery.data?.data ?? [];

  const emitActivityWith = async (
    fire: 'created' | 'updated',
    activity: DebugTemplate['activity'],
    targetFishId: string,
    id: string,
  ) => {
    setEmitting(true);
    try {
      const result = await service.createActivity({
        data: {
          id,
          type: activity.type,
          fishId: targetFishId,
          location: activity.location || null,
          space: activity.space || null,
          status: activity.status || null,
          amount: activity.amount !== '' ? Number(activity.amount) : null,
          eventType: activity.eventType || null,
          bookDate: toISO(activity.bookDate),
          activityDate: toISO(activity.activityDate),
          attachedInboxId: attachedInboxId || null,
          typeMeta:
            stylistName.trim() || leadOwnerName.trim() || roomTypes.trim()
              ? {
                  ...(stylistName.trim() ? { stylistName: stylistName.trim() } : {}),
                  ...(leadOwnerName.trim() ? { leadOwnerName: leadOwnerName.trim() } : {}),
                  ...(roomTypes.trim()
                    ? { rooms: roomTypes.split(',').map((r) => r.trim()).filter(Boolean) }
                    : {}),
                }
              : undefined,
        },
        fireCreatedTrigger: fire === 'created',
        fireUpdatedTrigger: fire === 'updated',
      });
      const fired: string[] = result?.meta?.eventsFired ?? [];
      const attachedInboxes: AttachedInbox[] = result?.meta?.attachedInboxes ?? [];
      const attachedWithLabels = attachedInboxes.map((attached) => ({
        ...attached,
        inboxLabel: attached.inboxId
          ? inboxOptions.find((o) => o.value === attached.inboxId)?.label ?? attached.inboxId
          : null,
      }));
      const stylistLabel =
        attachedWithLabels.find((attached) => attached.type === 'STYLIST')?.inboxLabel ?? null;
      setLastEmitResolved(!!stylistLabel);
      setLastEmitFrom(stylistLabel);
      const attachedSummary = attachedWithLabels
        .map(
          (attached) =>
            `${attached.type}: inbox ${attached.inboxLabel ?? 'none'}, user ${attached.userId ?? 'none'}`,
        )
        .join('; ');
      toastSuccess(
        `Activity emitted.${fired.length ? ` Fired: ${fired.join(', ')}.` : ''} Attached inboxes: ${
          attachedSummary || 'none'
        }`,
      );
      // A new activity id so the next emit isn't deduped as the same event.
      setActivityId(crypto.randomUUID());
    } catch (err) {
      console.error('Error emitting activity:', err);
      toastError('Unable to emit activity. Check the console.');
    } finally {
      setEmitting(false);
    }
  };

  const emitLapseWith = async (
    targetFishId: string,
    flowStageId: string,
    evaluateConditions: boolean,
    lastExecutedLocal: string,
  ) => {
    setEmitting(true);
    setLastLapseResult(null);
    try {
      const res = await service.emitDebugLapse({
        fishId: targetFishId,
        flowStageId,
        evaluate: evaluateConditions,
        lastStageExecutedAt: lastExecutedLocal
          ? toISO(lastExecutedLocal) ?? undefined
          : undefined,
      });
      const emitted = res?.data?.emitted !== false;
      const reason = res?.data?.reason as string | undefined;
      setLastLapseResult({ emitted, reason });
      if (emitted) {
        toastSuccess('Lapse event emitted.');
      } else {
        toastError(`Not lapsed: ${reason ?? 'conditions not met'}`);
      }
    } catch (err) {
      console.error('Error emitting lapse:', err);
      toastError('Unable to emit lapse. Check the console.');
    } finally {
      setEmitting(false);
    }
  };

  const emitInboundSmsWith = async (
    targetFishId: string,
    inboxId: string,
    body: string,
  ) => {
    setEmitting(true);
    setLastSmsResult(null);
    try {
      const res = await service.emitDebugInboundSms({
        fishId: targetFishId,
        inboxId,
        body,
      });
      const emitted = res?.data?.emitted === true;
      const resolvedFishId = (res?.data?.resolvedFishId as string | null) ?? null;
      const eventsFired: string[] = res?.meta?.eventsFired ?? [];
      setLastSmsResult({ emitted, resolvedFishId });
      if (eventsFired.length > 0) {
        toastSuccess(`Inbound SMS delivered. Fired: ${eventsFired.join(', ')}.`);
      } else {
        toastError('SMS stored, but its phone matched no fish, so no flow event fired.');
      }
    } catch (err) {
      console.error('Error emitting inbound SMS:', err);
      toastError('Unable to emit inbound SMS. Check the console.');
    } finally {
      setEmitting(false);
    }
  };

  const emitUpcomingWith = async (
    flowStageId: string,
    entityId: string,
    evaluateDue: boolean,
  ) => {
    setEmitting(true);
    setLastUpcomingResult(null);
    try {
      const res = await service.emitDebugUpcoming({
        flowStageId,
        entityId,
        evaluate: evaluateDue,
      });
      const result: UpcomingEmitResult = {
        emitted: res?.data?.emitted ?? 0,
        skippedReason: res?.data?.skippedReason,
        counts: res?.data?.counts,
        fishId: res?.data?.fishId,
      };
      setLastUpcomingResult(result);
      // The occurrence belongs to the booking's or subscription's Fish, not the
      // picked test fish; watch that Fish so its instances and trace show up.
      if (result.fishId && result.fishId !== fishId) {
        setFish({ value: result.fishId, label: `Fish ${result.fishId.slice(0, 8)}` });
      }
      if (result.emitted > 0) {
        toastSuccess('Upcoming event emitted.');
      } else {
        toastError(`Nothing emitted: ${result.skippedReason ?? 'not due, filtered out, or already sent'}`);
      }
    } catch (err) {
      console.error('Error emitting upcoming stage:', err);
      toastError('Unable to emit upcoming stage. Check the console.');
    } finally {
      setEmitting(false);
    }
  };

  const currentActivity = (): DebugTemplate['activity'] => ({
    type,
    location,
    space,
    status,
    amount,
    eventType,
    activityDate,
    bookDate,
  });

  const onEmit = () => {
    // An upcoming stage runs for an activity or subscription, not a picked fish.
    if (mode === 'upcoming') {
      if (!upcomingStageId || !upcomingEntityId.trim()) return;
      return emitUpcomingWith(upcomingStageId, upcomingEntityId.trim(), evaluate);
    }
    if (!fishId) return;
    if (mode === 'activity-created')
      return emitActivityWith('created', currentActivity(), fishId, activityId);
    if (mode === 'activity-updated')
      return emitActivityWith('updated', currentActivity(), fishId, activityId);
    if (mode === 'inbound-sms') {
      if (!smsInboxId || !smsBody.trim()) return;
      return emitInboundSmsWith(fishId, smsInboxId, smsBody.trim());
    }
    if (!lapseStageId) return;
    return emitLapseWith(fishId, lapseStageId, evaluate, lastStageExecutedAt);
  };

  // One-click replay: emit straight from the saved template values.
  const runTemplate = (t: DebugTemplate) => {
    applyTemplate(t);
    if (t.mode === 'upcoming') {
      if (!t.upcoming?.stageId || !t.upcoming.entityId.trim()) {
        toastError('Template has no upcoming stage or entity id saved.');
        return;
      }
      return emitUpcomingWith(t.upcoming.stageId, t.upcoming.entityId.trim(), t.evaluate ?? true);
    }
    const targetFishId = t.fish?.value;
    if (!targetFishId) {
      toastError('Template has no test fish saved.');
      return;
    }
    if (t.mode === 'inbound-sms') {
      if (!t.inboundSms?.inboxId || !t.inboundSms.body.trim()) {
        toastError('Template has no inbox or message saved.');
        return;
      }
      return emitInboundSmsWith(
        targetFishId,
        t.inboundSms.inboxId,
        t.inboundSms.body.trim(),
      );
    }
    if (t.mode === 'lapse') {
      if (!t.lapseStageId) {
        toastError('Template has no lapse stage saved.');
        return;
      }
      return emitLapseWith(
        targetFishId,
        t.lapseStageId,
        t.evaluate ?? true,
        t.lastStageExecutedAt ?? '',
      );
    }
    return emitActivityWith(
      t.mode === 'activity-created' ? 'created' : 'updated',
      t.activity,
      targetFishId,
      crypto.randomUUID(),
    );
  };

  const isEmitInputMissing = () => {
    if (mode === 'upcoming') return !upcomingStageId || !upcomingEntityId.trim();
    if (!fishId) return true;
    if (mode === 'lapse') return !lapseStageId;
    if (mode === 'inbound-sms') return !smsInboxId || !smsBody.trim();
    return false;
  };
  const emitDisabled = emitting || isEmitInputMissing();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[560px] max-w-[90vw] sm:max-w-[560px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>Flow Debugger</SheetTitle>
          </SheetHeader>

          <SheetBody className="grow space-y-5 overflow-auto">
            {/* Templates */}
            <section className="space-y-2">
              <label className={labelClass}>Templates</label>
              {templates.length > 0 ? (
                <div className="space-y-1">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between rounded border border-gray-200 px-2 py-1 text-sm"
                    >
                      <button
                        type="button"
                        className="truncate text-left text-gray-700 hover:underline"
                        onClick={() => applyTemplate(t)}
                        title="Load into the form"
                      >
                        {t.name}
                        <span className="ml-2 text-xs text-gray-400">
                          {t.mode}
                          {t.fish ? ` · ${t.fish.label}` : ''}
                        </span>
                      </button>
                      <div className="ml-2 flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="rounded bg-gray-800 px-2 py-0.5 text-xs text-white disabled:opacity-40"
                          onClick={() => runTemplate(t)}
                          disabled={emitting}
                        >
                          Run
                        </button>
                        <button
                          type="button"
                          className="text-xs text-red-500"
                          onClick={() => deleteTemplate(t.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No saved templates.</p>
              )}
              <div className="flex gap-2">
                <input
                  className={fieldClass}
                  placeholder="Template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
                <button
                  type="button"
                  className="mt-1 shrink-0 rounded bg-gray-100 px-2 text-xs text-gray-700 disabled:opacity-40"
                  onClick={saveTemplate}
                  disabled={!templateName.trim()}
                >
                  Save current
                </button>
              </div>
            </section>

            {/* Test target */}
            <section>
              <label className={labelClass}>Test fish</label>
              <FishSearchSelect value={fish} onChange={setFish} />
            </section>

            {/* Emit panel */}
            <section className="space-y-3 rounded border border-gray-200 p-3">
              <div className="flex gap-2">
                {(
                  [
                    ['activity-created', 'Activity Created'],
                    ['activity-updated', 'Activity Updated'],
                    ['lapse', 'Lapse Detected'],
                    ['upcoming', 'Upcoming'],
                    ['inbound-sms', 'Inbound SMS'],
                  ] as [EmitMode, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={`rounded px-2 py-1 text-xs ${
                      mode === value
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {mode === 'inbound-sms' ? (
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Inbox texted (TO)</label>
                    <select
                      className={fieldClass}
                      value={smsInboxId}
                      onChange={(e) => setSmsInboxId(e.target.value)}
                    >
                      <option value="">Select an SMS inbox…</option>
                      {inboxOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Message</label>
                    <textarea
                      className={fieldClass}
                      rows={3}
                      value={smsBody}
                      onChange={(e) => setSmsBody(e.target.value)}
                      placeholder="What the test fish texts"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Delivered through the real inbound webhook path: the message lands
                      in this inbox, its owner is notified, and INBOUND_SMS fires for the
                      fish the sender's phone resolves to. An opt-out keyword such as STOP
                      turns off SMS opt-in for every fish on that phone and fires
                      SMS_UNSUBSCRIBED for each one that was opted in.
                    </p>
                  </div>
                  {lastSmsResult && !lastSmsResult.emitted && (
                    <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                      Stored in the inbox, but the fish's phone matched no fish, so no
                      flow event fired.
                    </p>
                  )}
                  {lastSmsResult?.emitted &&
                    lastSmsResult.resolvedFishId &&
                    lastSmsResult.resolvedFishId !== fishId && (
                      <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                        The phone resolved to a different fish (
                        {lastSmsResult.resolvedFishId.slice(0, 8)}), so its flows fired, not
                        the selected fish's.
                      </p>
                    )}
                </div>
              ) : mode === 'upcoming' ? (
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Upcoming stage</label>
                    <select
                      className={fieldClass}
                      value={upcomingStageId}
                      onChange={(e) => setUpcomingStageId(e.target.value)}
                    >
                      <option value="">Select an upcoming stage…</option>
                      {upcomingStages.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title || s.id}
                        </option>
                      ))}
                    </select>
                    {upcomingStages.length === 0 && (
                      <p className="mt-1 text-xs text-gray-400">
                        This flow has no saved upcoming stages.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Activity id or subscription id</label>
                    <input
                      className={fieldClass}
                      value={upcomingEntityId}
                      onChange={(e) => setUpcomingEntityId(e.target.value)}
                      placeholder="The booking (activity stages) or subscription (renewal stages)"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={evaluate}
                      onChange={(e) => setEvaluate(e.target.checked)}
                    />
                    Only emit if it is due now (unchecked: emit even if the send time is later)
                  </label>
                  <p className="text-xs text-gray-500">
                    Runs the same selection as the sweep for this one booking or renewal:
                    the stage's conditions, its schedule and duplicates. Something already
                    sent on this stage is never sent again.
                  </p>
                  {lastUpcomingResult && lastUpcomingResult.emitted === 0 && (
                    <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                      Nothing emitted: {describeUpcomingMiss(lastUpcomingResult)}
                    </p>
                  )}
                  {lastUpcomingResult?.fishId && (
                    <p className="text-xs text-gray-500">
                      Live log is watching this occurrence's Fish ({lastUpcomingResult.fishId.slice(0, 8)}).
                    </p>
                  )}
                </div>
              ) : mode === 'lapse' ? (
                <div>
                  <label className={labelClass}>Lapse stage</label>
                  <select
                    className={fieldClass}
                    value={lapseStageId}
                    onChange={(e) => setLapseStageId(e.target.value)}
                  >
                    <option value="">Select a lapse stage…</option>
                    {lapseStages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title || s.id}
                      </option>
                    ))}
                  </select>
                  {lapseStages.length === 0 && (
                    <p className="mt-1 text-xs text-gray-400">
                      This flow has no lapse stages.
                    </p>
                  )}
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={evaluate}
                      onChange={(e) => setEvaluate(e.target.checked)}
                    />
                    Evaluate conditions (only emit if the fish has lapsed)
                  </label>
                  {evaluate && (
                    <div>
                      <label className={labelClass}>
                        Simulate last stage executed at (optional)
                      </label>
                      <input
                        type="datetime-local"
                        className={fieldClass}
                        value={lastStageExecutedAt}
                        onChange={(e) => setLastStageExecutedAt(e.target.value)}
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        Overrides the fish's real predecessor execution time for the
                        anchor check.
                      </p>
                    </div>
                  )}
                  {lastLapseResult && !lastLapseResult.emitted && (
                    <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                      Not lapsed: {lastLapseResult.reason ?? 'conditions not met'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className={labelClass}>Activity id</label>
                    <div className="flex gap-2">
                      <input
                        className={fieldClass}
                        value={activityId}
                        onChange={(e) => setActivityId(e.target.value)}
                      />
                      <button
                        type="button"
                        className="mt-1 shrink-0 rounded bg-gray-100 px-2 text-xs text-gray-700"
                        onClick={() => setActivityId(crypto.randomUUID())}
                      >
                        Regenerate
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Type</label>
                    <select
                      className={fieldClass}
                      value={type}
                      onChange={(e) =>
                        setType(
                          e.target.value as (typeof ACTIVITY_TYPES)[number],
                        )
                      }
                    >
                      {ACTIVITY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Status</label>
                    <input
                      className={fieldClass}
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    />
                  </div>
                  {type === 'Booked Event' && (
                    <div>
                      <label className={labelClass}>Event type</label>
                      <input
                        className={fieldClass}
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        placeholder="e.g. wedding"
                      />
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Amount</label>
                    <input
                      className={fieldClass}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      inputMode="decimal"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Location</label>
                    <input
                      className={fieldClass}
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Space</label>
                    <input
                      className={fieldClass}
                      value={space}
                      onChange={(e) => setSpace(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Activity date</label>
                    <input
                      type="datetime-local"
                      className={fieldClass}
                      value={activityDate}
                      onChange={(e) => setActivityDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Book date</label>
                    <input
                      type="datetime-local"
                      className={fieldClass}
                      value={bookDate}
                      onChange={(e) => setBookDate(e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Stylist name</label>
                    <input
                      className={fieldClass}
                      value={stylistName}
                      onChange={(e) => setStylistName(e.target.value)}
                      placeholder="matches a staff user's name"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Sent as typeMeta.stylistName. With "Attached inbox" set to
                      None, the server hydrates the FROM inbox by matching this
                      name to a staff user with an active dedicated SMS inbox.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Lead owner name</label>
                    <input
                      className={fieldClass}
                      value={leadOwnerName}
                      onChange={(e) => setLeadOwnerName(e.target.value)}
                      placeholder="matches a staff user's name"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Sent as typeMeta.leadOwnerName. The server matches it to a staff
                      user for the LEAD_OWNER attached inbox: the "Lead Owner Inbox"
                      sender and the "Lead Owner" recipient.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Room type(s)</label>
                    <input
                      className={fieldClass}
                      value={roomTypes}
                      onChange={(e) => setRoomTypes(e.target.value)}
                      placeholder="e.g. King, Suite (comma-separated)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Sent as typeMeta.rooms; the emitted activity event carries them
                      so a stage condition on activity → Room Type can match.
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Attached inbox (FROM)</label>
                    <select
                      className={fieldClass}
                      value={attachedInboxId}
                      onChange={(e) => setAttachedInboxId(e.target.value)}
                    >
                      <option value="">None — resolve from stylist name</option>
                      {inboxOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Attaches this inbox to the event so a send action set to "Send
                      from → Stylist Inbox" routes FROM it.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={onEmit}
                disabled={emitDisabled}
                className="w-full rounded bg-gray-800 py-2 text-sm text-white disabled:opacity-40"
              >
                {emitting ? 'Emitting…' : 'Emit event'}
              </button>
              {mode !== 'inbound-sms' && lastEmitResolved !== null && (
                <p
                  className={`rounded px-2 py-1 text-xs ${
                    lastEmitResolved
                      ? 'bg-green-50 text-green-800'
                      : 'bg-amber-50 text-amber-800'
                  }`}
                >
                  {lastEmitResolved
                    ? `Hydrated FROM inbox: ${lastEmitFrom}`
                    : 'No FROM inbox resolved (name unmatched, ambiguous, or no dedicated number)'}
                </p>
              )}
              {!fishId && mode !== 'upcoming' && (
                <p className="text-xs text-gray-400">
                  Select a test fish to emit events.
                </p>
              )}
            </section>

            {/* Live log */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">
                  Live log
                </h3>
                <span className="text-xs text-gray-400">
                  {fishId ? 'polling…' : 'pick a fish, or emit an upcoming event'}
                </span>
              </div>

              {/* Instances */}
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Flow instances ({instances.length})
                </p>
                <div className="mt-1 space-y-1">
                  {instances.map((inst) => (
                    <div
                      key={inst.id}
                      className="rounded border border-gray-200 px-2 py-1 text-xs"
                    >
                      <span className="font-mono text-gray-500">
                        {inst.id.slice(0, 8)}
                      </span>
                      <span className="ml-2 text-gray-700">
                        {(inst.flowStageResults?.length ?? 0)} stage result(s)
                      </span>
                    </div>
                  ))}
                  {instances.length === 0 && (
                    <p className="text-xs text-gray-400">No instances yet.</p>
                  )}
                </div>
              </div>

              {/* Trace timeline (stage results, skips, trigger receipts) */}
              <div>
                <p className="text-xs font-medium text-gray-500">
                  Execution trace ({entries.length})
                </p>
                <div className="mt-1 space-y-1">
                  {entries.map((e, i) => (
                    <div
                      key={`${e.ts}-${i}`}
                      className="rounded border border-gray-100 bg-gray-50 px-2 py-1 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`rounded px-1.5 py-0.5 font-medium ${
                            OUTCOME_STYLES[e.outcome] ??
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {e.outcome}
                        </span>
                        <span className="text-gray-400">
                          {new Date(e.ts).toLocaleTimeString()}
                        </span>
                      </div>
                      {(e.flowStageId || e.detail) && (
                        <div className="mt-1 text-gray-500">
                          {e.flowStageId && (
                            <span className="font-mono">
                              stage {e.flowStageId.slice(0, 8)}
                            </span>
                          )}
                          {e.detail && (
                            <span className="ml-2">
                              {JSON.stringify(e.detail)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {entries.length === 0 && (
                    <p className="text-xs text-gray-400">
                      No trace entries yet.
                    </p>
                  )}
                </div>
              </div>
            </section>
          </SheetBody>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FlowDebugSlideout;
